/* import __COLOCATED_TEMPLATE__ from './sq-checkbox.hbs'; */
/**
An Atomic Checkbox

This component is meant to be used without a label, such as as a column in a table. If you
need a checkbox with a label, see the [sq-option-checkbox](./sq-option-checkbox.html) component.

```example
@title Basic Usage
@description

@hbs
<p>Unchecked<p>
{{sq-checkbox}}

<p>Checked<p>
{{sq-checkbox checked=true}}

<p>Indeterminate<p>
{{sq-checkbox checked="indeterminate"}}

<p>Unchecked Disabled<p>
{{sq-checkbox disabled=true}}

<p>Checked Disabled<p>
{{sq-checkbox checked=true disabled=true}}

<p>Indeterminate Disabled<p>
{{sq-checkbox checked="indeterminate" disabled=true}}

```
@param {String} name - A name for this field.
@param {Any} value - A value that this checkbox represents **NOTE:** This param is not to be used to represent the checkbox's state (e.g. a boolean for checked or unchecked), use the `checked` param instead.
@param {Boolean|String} [checked=false] - true, false or 'indeterminate'
@param {Boolean} [disabled=false] - Disabled the checkbox
@param {Number} [tabindex=0] - Passes through to the input giving it a tabindex for keyboard navigation
@param {String} [checkboxId] - Adds the id to the checkbox input
@param {String} [testSelectorValue] - For use cases where value is an object, pass in this prop to specify test selector value
@param {Function} action - An action to perform when the value of 'checked' changes. Is passed the new 'checked' value of the checkbox
*/

import Component from '@ember/component';
import { computed } from '@ember/object';
import { equal, readOnly } from '@ember/object/computed';
import IconCheckmark from './svg/icon-checkmark';
import IconUndetermined from './svg/icon-undetermined';
export const CHECKBOX_STATE = {
  UNCHECKED: false,
  CHECKED: true,
  INDETERMINATE: 'indeterminate'
};

/*

Probably don't mess with this. Checkboxes are deceivingly complicated.
-jbiggs

---Native HTML behavior of checkbox inputs----
- Browsers treat 'checked' and 'indeterminate' as two separate concerns
when it comes to checkboxes
- Checkboxes should have the 'checked' property when they are actually
checked *and* when they are 'indeterminate'
- HTML provides a 'checked' attribute for <input> elements, that you
can pass an attribute to in the component template, but the 'indeterminate'
property can only be set throught javascript

---

This component uses a faux checkbox in order to provide a custom style
for checkboxes, but also includes a valid checkbox input that is kept
in-sync with the state of the matching faux checkbox

*/

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: 'span',
  classNames: ['option-toggle', 'checkbox'],
  classNameBindings: ['checked:checkbox--is-checked', 'checked:option-toggle--is-selected', 'indeterminate:checkbox--is-indeterminate', 'disabled:option-toggle--is-disabled', 'disabled:checkbox--is-disabled'],
  attributeBindings: ['data-test-checkbox', 'data-test-checkbox-checked'],
  'data-test-checkbox': computed('value', 'testSelectorValue', function () {
    return this.testSelectorValue || this.value || true;
  }),
  'data-test-checkbox-checked': readOnly('checked'),
  // passed in, true, false, or 'indeterminate'
  // It is passed to the 'checked' attribute on the
  // checkbox input and will evalute to 'true' if
  // either 'true' or 'indeterminate' is passed
  checked: false,
  indeterminate: equal('checked', CHECKBOX_STATE.INDETERMINATE),
  // The svg icon serving as either a checkmark or an indeterminate line
  // We use svgs even for the simpler undetermined line in order to
  // ensure line-weight consistency and porportion of the marks always match
  checkmarkSvg: computed('checked', function () {
    switch (this.get('checked')) {
      case CHECKBOX_STATE.CHECKED:
        return IconCheckmark;
      case CHECKBOX_STATE.INDETERMINATE:
        return IconUndetermined;
      default:
        return null;
    }
  }),
  actions: {
    onChange(event) {
      const checked = event.target.checked;
      if (this.action) {
        this.action(checked, this.get('value'));
      }
      this.set('checked', checked);
    }
  }
});